import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'react-hot-toast';
import {
  Bell,
  Mail,
  MessageSquare,
  Calendar,
  CheckSquare,
  Users,
  Clock,
  Smartphone,
  Globe,
  AlertCircle,
  Save,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

// Supabase client initialization
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const NotificationPreferences = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Bildirim tercihlerinin state'leri
  const [preferences, setPreferences] = useState({
    email_tasks: true,
    email_mentions: true,
    email_reminders: true,
    email_team: true,
    email_security: true,
    push_tasks: true,
    push_mentions: true,
    push_reminders: true,
    push_team: true,
    push_security: true,
    desktop_tasks: true,
    desktop_mentions: true,
    desktop_reminders: true,
    desktop_team: true,
    desktop_security: true,
    quiet_hours_enabled: true,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    all_notifications_enabled: true,
    weekend_notifications_enabled: true,
    notification_sound_enabled: true,
    notification_sound_type: 'default',
    vibration_enabled: true
  });

  // Bildirim türleri
  const notificationTypes = [
    {
      id: 'tasks',
      title: 'Görev Bildirimleri',
      description: 'Görev atamaları, güncellemeler ve son tarih hatırlatmaları',
      icon: CheckSquare
    },
    {
      id: 'mentions',
      title: 'Bahsetmeler',
      description: 'Biri sizi bir görevde veya yorumda etiketlediğinde',
      icon: MessageSquare
    },
    {
      id: 'reminders',
      title: 'Hatırlatıcılar',
      description: 'Yaklaşan görevler ve toplantılar için hatırlatmalar',
      icon: Calendar
    },
    {
      id: 'team',
      title: 'Ekip Güncellemeleri',
      description: 'Ekip üyeleri ve proje değişiklikleri hakkında bildirimler',
      icon: Users
    },
    {
      id: 'security',
      title: 'Güvenlik Uyarıları',
      description: 'Hesap güvenliği ve oturum açma bildirimleri',
      icon: AlertCircle
    }
  ];

  // Bildirim kanalları
  const channels = [
    {
      id: 'email',
      title: 'E-posta Bildirimleri',
      description: 'E-posta adresinize gönderilecek bildirimler',
      icon: Mail
    },
    {
      id: 'push',
      title: 'Mobil Bildirimler',
      description: 'Mobil cihazınıza gönderilecek anlık bildirimler',
      icon: Smartphone
    },
    {
      id: 'desktop',
      title: 'Masaüstü Bildirimleri',
      description: 'Tarayıcı üzerinden alacağınız bildirimler',
      icon: Globe
    }
  ];

  // Tercihleri güncelle
  const handleToggle = (channel, type) => {
    const key = `${channel}_${type}`;
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Sessiz saat güncelleme
  const handleQuietHoursChange = (type, value) => {
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // Genel ayar güncelleme
  const handleSettingToggle = (setting) => {
    setPreferences(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  // Ses tipi güncelleme
  const handleSoundTypeChange = (value) => {
    setPreferences(prev => ({
      ...prev,
      notification_sound_type: value
    }));
  };

  // Kullanıcının tercihlerini veritabanından çek
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        setIsInitialLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          navigate('/auth/signin');
          return;
        }

        // First check if the user already has preferences using maybeSingle() instead of single()
        const { data: existingPrefs, error: fetchError } = await supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (fetchError && fetchError.code !== 'PGRST116') {
          // If there's an error other than "no results found"
          throw fetchError;
        }

        // If preferences already exist, use them
        if (existingPrefs) {
          console.log('Found existing preferences:', existingPrefs);
          setPreferences(existingPrefs);
          return;
        }

        console.log('No existing preferences found, creating defaults');
        // No preferences found, create new ones
        const defaultPrefs = {
          user_id: session.user.id,
          email_tasks: true,
          email_mentions: true,
          email_reminders: true,
          email_team: true,
          email_security: true,
          push_tasks: true,
          push_mentions: true,
          push_reminders: true,
          push_team: true,
          push_security: true,
          desktop_tasks: true,
          desktop_mentions: true,
          desktop_reminders: true,
          desktop_team: true,
          desktop_security: true,
          quiet_hours_enabled: true,
          quiet_hours_start: '22:00',
          quiet_hours_end: '08:00',
          all_notifications_enabled: true,
          weekend_notifications_enabled: true,
          notification_sound_enabled: true,
          notification_sound_type: 'default',
          vibration_enabled: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        const { data: newPrefs, error: createError } = await supabase
          .from('notification_preferences')
          .insert([defaultPrefs])
          .select()
          .single();

        if (createError) {
          // Check if the error is due to a duplicate key
          if (createError.code === '23505') {
            // Duplicate key, try fetching again as it might have been created in another session
            const { data: retryPrefs, error: retryError } = await supabase
              .from('notification_preferences')
              .select('*')
              .eq('user_id', session.user.id)
              .maybeSingle();
            
            if (retryError) throw retryError;
            
            if (retryPrefs) {
              console.log('Retrieved preferences after duplicate error:', retryPrefs);
              setPreferences(retryPrefs);
              return;
            }
          }
          throw createError;
        }

        setPreferences(newPrefs);
      } catch (error) {
        console.error('Error fetching preferences:', error);
        toast.error('Tercihler yüklenirken bir hata oluştu.');
      } finally {
        setIsInitialLoading(false);
        setIsLoading(false);
      }
    };

    fetchPreferences();
  }, [navigate]);

  // Tercihleri kaydet
  const handleSave = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/auth/signin');
        return;
      }

      // Add updated timestamp and user_id to ensure the record exists
      const updatedPreferences = {
        ...preferences,
        user_id: session.user.id,
        updated_at: new Date().toISOString()
      };

      // First check if preferences already exist
      const { data: existingPrefs, error: checkError } = await supabase
        .from('notification_preferences')
        .select('id')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking for existing preferences:', checkError);
      }

      let error;

      if (existingPrefs) {
        // If record exists, update it
        console.log('Updating existing preferences');
        const result = await supabase
          .from('notification_preferences')
          .update(updatedPreferences)
          .eq('user_id', session.user.id);
        
        error = result.error;
      } else {
        // If no record exists, insert one
        console.log('Creating new preferences record');
        const result = await supabase
          .from('notification_preferences')
          .insert([{
            ...updatedPreferences,
            created_at: new Date().toISOString()
          }]);
        
        error = result.error;
      }

      if (error) throw error;

      toast.success('Tercihleriniz başarıyla kaydedildi.');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Tercihler kaydedilirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  // İlk yükleme sırasında loading göster
  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 dark:text-gray-400">Tercihler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Üst Başlık */}
        <div className="mb-8 sticky top-10 z-10 backdrop-blur-lg border-b border-white/900 dark:border-white/40  ">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Geri Dön
          </button>
          
          <div className="flex items-start justify-between m-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Bildirim Tercihleri
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400 mb-4">
                Hangi bildirimler hakkında, nasıl haberdar edilmek istediğinizi yönetin.
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Kaydediliyor...</span>
                </>
              ) : (
                <>
                  <Save className="w-6 h-6 mr-2" />
                  <span>Değişiklikleri Kaydet</span>
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Ana İçerik */}
        <div className="space-y-6">
          {/* Bildirim Kanalları */}
          {channels.map(channel => (
            <motion.div
              key={channel.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Kanal Başlığı */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${
                    isDark ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <channel.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {channel.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {channel.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bildirim Türleri */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {notificationTypes.map(type => (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <type.icon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          {type.title}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {type.description}
                        </p>
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <button
                      onClick={() => handleToggle(channel.id, type.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                        preferences[`${channel.id}_${type.id}`]
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      <span className="sr-only">
                        {preferences[`${channel.id}_${type.id}`] ? 'Açık' : 'Kapalı'}
                      </span>
                      <motion.span
                        initial={false}
                        animate={{
                          translateX: preferences[`${channel.id}_${type.id}`] ? '20px' : '2px'
                        }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        className={`inline-block h-5 w-5 transform rounded-full ${
                          preferences[`${channel.id}_${type.id}`]
                            ? 'bg-white'
                            : 'bg-white dark:bg-gray-400'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}

          {/* Bilgilendirme Kartı */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Bildirim Özelleştirme
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Bildirim davranışlarını ve tercihlerinizi özelleştirin
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Tüm Bildirimleri Aç/Kapat */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Tüm Bildirimleri {preferences.all_notifications_enabled ? 'Kapat' : 'Aç'}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Tüm bildirim kanallarını tek seferde yönetin
                  </p>
                </div>
                <button
                  onClick={() => handleSettingToggle('all_notifications_enabled')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                    preferences.all_notifications_enabled
                      ? 'bg-primary-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <motion.span
                    initial={false}
                    animate={{
                      translateX: preferences.all_notifications_enabled ? '20px' : '2px'
                    }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="inline-block h-5 w-5 transform rounded-full bg-white"
                  />
                </button>
              </div>

              {/* Sessiz Saatler */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Sessiz Saatler
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Belirli saatlerde bildirimleri otomatik olarak sessize al
                    </p>
                  </div>
                  <button
                    onClick={() => handleSettingToggle('quiet_hours_enabled')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      preferences.quiet_hours_enabled
                        ? 'bg-primary-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <motion.span
                      initial={false}
                      animate={{
                        translateX: preferences.quiet_hours_enabled ? '20px' : '2px'
                      }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="inline-block h-5 w-5 transform rounded-full bg-white"
                    />
                  </button>
                </div>

                {preferences.quiet_hours_enabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Başlangıç Saati
                      </label>
                      <input
                        type="time"
                        value={preferences.quiet_hours_start}
                        onChange={(e) => handleQuietHoursChange('quiet_hours_start', e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bitiş Saati
                      </label>
                      <input
                        type="time"
                        value={preferences.quiet_hours_end}
                        onChange={(e) => handleQuietHoursChange('quiet_hours_end', e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 focus:border-transparent"
                      />

                      
                    </div>

                    
                    
                  </div>

                  
                  
                )}
                
              </div>

              {/* Hafta Sonu Bildirimleri */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Hafta Sonu Bildirimleri
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Cumartesi ve Pazar günleri bildirim almak istiyorum
                  </p>
                </div>
                <button
                  onClick={() => handleSettingToggle('weekend_notifications_enabled')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                    preferences.weekend_notifications_enabled
                      ? 'bg-primary-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <motion.span
                    initial={false}
                    animate={{
                      translateX: preferences.weekend_notifications_enabled ? '20px' : '2px'
                    }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="inline-block h-5 w-5 transform rounded-full bg-white"
                  />
                </button>
              </div>

              {/* Bildirim Sesi */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Bildirim Sesi
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Bildirim geldiğinde ses çal
                    </p>
                  </div>
                  <button
                    onClick={() => handleSettingToggle('notification_sound_enabled')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      preferences.notification_sound_enabled
                        ? 'bg-primary-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <motion.span
                      initial={false}
                      animate={{
                        translateX: preferences.notification_sound_enabled ? '20px' : '2px'
                      }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="inline-block h-5 w-5 transform rounded-full bg-white"
                    />
                  </button>
                </div>

                {preferences.notification_sound_enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Ses Tipi
                    </label>
                    <select
                      value={preferences.notification_sound_type}
                      onChange={(e) => handleSoundTypeChange(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="default">Varsayılan</option>
                      <option value="chime">Çan</option>
                      <option value="bell">Zil</option>
                      <option value="melody">Melodi</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Titreşim */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Titreşim
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Bildirim geldiğinde titreşim kullan
                  </p>
                </div>
                <button
                  onClick={() => handleSettingToggle('vibration_enabled')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                    preferences.vibration_enabled
                      ? 'bg-primary-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <motion.span
                    initial={false}
                    animate={{
                      translateX: preferences.vibration_enabled ? '20px' : '2px'
                    }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="inline-block h-5 w-5 transform rounded-full bg-white"
                  />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Bilgilendirme Kartı */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4"
          >
            <div className="flex gap-3">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                  Bildirim Ayarları Hakkında
                </h4>
                <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                  Tüm bildirim ayarlarınız otomatik olarak kaydedilir ve tüm cihazlarınızda senkronize edilir.
                  Sessiz saatler ve diğer özelleştirmeler anında uygulanır.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;

